import { Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { LanguageRoute } from "./components/common/LanguageRoute";
import { AtRouteHandler } from "./components/common/AtRouteHandler";
import NotFound from "./pages/NotFound";
import { useLoginStore, type LoginStore } from "@/stores/loginStore";

// Customer routes (regular users managing their events)
import AdminDashboard from "./pages/customer/Dashboard";
import EventManagement from "./pages/customer/EventManagement";
import OnboardingWizardPage from "./pages/customer/onboarding/OnboardingWizardPage";
import { PaymentSettingsRouteGate } from "./components/common/PaymentSettingsRouteGate";
import { PaymentsRedirect } from "./components/common/PaymentsRedirect";
import PachetePage from "./pages/customer/PachetePage";
import AccountSettings from "./pages/customer/AccountSettings";

// Administrator routes (Jubiel employees with user_admin = true)
import TemplateManagement from "./pages/administrator/TemplateManagement";
import Management from "./pages/administrator/Management";
import EventsListing from "./pages/administrator/EventsListing";
import SuperadminUserProfile from "./pages/administrator/SuperadminUserProfile";
import CampaignManagement from "./pages/administrator/CampaignManagement";
import CampaignEditor from "./pages/administrator/CampaignEditor";
import { RequireSuperadmin } from "./components/admin/RequireSuperadmin";

// Public routes
import LandingPage from "./pages/public/LandingPage";
import RegistrationPage from "./pages/public/RegistrationPage";
import LoginPage from "./pages/public/LoginPage";
import SuperadminLoginPage from "./pages/public/SuperadminLoginPage";
import EmailVerificationPage from "./pages/public/EmailVerificationPage";
import ForgotPasswordPage from "./pages/public/ForgotPasswordPage";
import ResetPasswordPage from "./pages/public/ResetPasswordPage";
import EventPublicPage from "./pages/public/EventPublicPage";
import { PaymentRouteGate } from "./components/common/PaymentRouteGate";
import LogoutPage from "./pages/public/LogoutPage";
import RSVPDocumentationPage from "./pages/developer/RSVPDocumentationPage";
import GiftContributionsDocumentationPage from "./pages/developer/GiftContributionsDocumentationPage";
import CookiesPolicyPage from "./pages/public/CookiesPolicyPage";
import TermsPage from "./pages/public/TermsPage";
import PrivacyPolicyPage from "./pages/public/PrivacyPolicyPage";
import DataProcessingNoticePage from "./pages/public/DataProcessingNoticePage";
import CampaignQuestionnairePage from "./pages/public/CampaignQuestionnairePage";
import CampaignClaimPage from "./pages/public/CampaignClaimPage";
import BlogIndexPage from "./pages/public/BlogIndexPage";
import BlogArticlePage from "./pages/public/BlogArticlePage";
import FaqPage from "./pages/public/FaqPage";

function AppRoutes() {
  const location = useLocation();
  const clearLoginError = useLoginStore((state: LoginStore) => state.clearError);

  useEffect(() => {
    const isLoginPage = location.pathname.endsWith("/login");
    if (!isLoginPage) {
      clearLoginError();
    }
  }, [location.pathname, clearLoginError]);

  return (
    <Routes>
      {/* Public event route at root level - /@{slug} - must come BEFORE locale routes */}
      {/* Use catch-all that checks for @ prefix - this ensures @ routes are handled correctly */}
      <Route path="/*" element={<AtRouteHandler />} />

      {/* Language-aware routes - require locale prefix */}
      {/* Match only valid locales (ro, en) to prevent @ from being treated as locale */}
      <Route path="/ro/*" element={<LanguageRoute />}>
        {/* Nested routes for ro locale */}
        {/* Public routes - specific routes must come before :slug */}
        <Route path="login" element={<LoginPage />} />
        <Route path="superadmin/login" element={<SuperadminLoginPage />} />
        <Route path="management/login" element={<SuperadminLoginPage />} />
        <Route path="register" element={<RegistrationPage />} />
        <Route path="payment" element={<PaymentRouteGate />} />
        <Route path="email-verification" element={<EmailVerificationPage />} />
        <Route path="forgot-password" element={<ForgotPasswordPage />} />
        <Route path="reset-password" element={<ResetPasswordPage />} />
        <Route path="logout" element={<LogoutPage />} />
        <Route path="cookies-policy" element={<CookiesPolicyPage />} />
        <Route path="terms" element={<TermsPage />} />
        <Route path="privacy" element={<PrivacyPolicyPage />} />
        {/* JUB-105: point-of-collection GDPR notice for RSVP/gift consent links */}
        <Route path="data-processing-notice" element={<DataProcessingNoticePage />} />
        {/* JUB-123 / JUB-100: public product guides and FAQ */}
        <Route path="blog" element={<BlogIndexPage />} />
        <Route path="blog/:slug" element={<BlogArticlePage />} />
        <Route path="faq" element={<FaqPage />} />

        {/* Public campaign routes (discounts/vouchers) */}
        <Route path="campaign/:uuid/questionnaire" element={<CampaignQuestionnairePage />} />
        <Route path="campaign/:uuid/claim" element={<CampaignClaimPage />} />

        <Route index element={<LandingPage />} />

              {/* Developer documentation routes (only when VITE_ENABLE_DEV_DOCS is enabled) */}
              {import.meta.env.VITE_ENABLE_DEV_DOCS === "true" && (
                <>
                  <Route path="dev/rsvp-docs" element={<RSVPDocumentationPage />} />
                  <Route path="dev/gift-contributions-docs" element={<GiftContributionsDocumentationPage />} />
                </>
              )}

        {/* User routes (regular customers - protected) */}
        <Route path="evenimente" element={<AdminDashboard />} />
        <Route path="events" element={<AdminDashboard />} />
        <Route path="editeaza-eveniment/:id" element={<EventManagement />} />
        <Route path="edit-event/:id" element={<EventManagement />} />
        {/* JUB-154: event creation wizard */}
        <Route path="eveniment-nou" element={<OnboardingWizardPage />} />
        <Route path="eveniment-nou/:uuid/:step" element={<OnboardingWizardPage />} />
        <Route path="new-event" element={<OnboardingWizardPage />} />
        <Route path="new-event/:uuid/:step" element={<OnboardingWizardPage />} />
        
        {/* Public event routes */}
        <Route path="events/:slug" element={<EventPublicPage />} />
        <Route path="evenimente/:slug" element={<EventPublicPage />} />
        {/* Direct event slug route (without prefix) - must come after specific routes */}
        <Route path=":slug" element={<EventPublicPage />} />
        <Route path="plati" element={<PaymentsRedirect />} />
        <Route path="payments" element={<PaymentsRedirect />} />
        <Route path="pachete" element={<PachetePage />} />
        <Route path="payment-settings" element={<PachetePage />} />
        <Route path="conturi-conectate" element={<PaymentSettingsRouteGate />} />
        <Route path="connected-accounts" element={<PaymentSettingsRouteGate />} />
        <Route path="setari" element={<AccountSettings />} />
        <Route path="settings" element={<AccountSettings />} />

        {/* Administrator routes (Jubiel employees only - protected, all use /management/* prefix) */}
        <Route path="management/templates" element={<RequireSuperadmin><TemplateManagement /></RequireSuperadmin>} />
        <Route path="management/events" element={<RequireSuperadmin><EventsListing /></RequireSuperadmin>} />
        <Route path="management/users/:userId" element={<RequireSuperadmin><SuperadminUserProfile /></RequireSuperadmin>} />
        <Route path="management/campaigns" element={<RequireSuperadmin><CampaignManagement /></RequireSuperadmin>} />
        <Route path="management/campaigns/:uuid" element={<RequireSuperadmin><CampaignEditor /></RequireSuperadmin>} />
        <Route path="management" element={<RequireSuperadmin><Management /></RequireSuperadmin>} />
        {/* JUB-184: anything deeper that no route above claims */}
        <Route path="*" element={<NotFound />} />
      </Route>

      <Route path="/en/*" element={<LanguageRoute />}>
        {/* Nested routes for en locale - same structure as ro */}
        {/* Public routes */}
        <Route index element={<LandingPage />} />
        <Route path="register" element={<RegistrationPage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="superadmin/login" element={<SuperadminLoginPage />} />
        <Route path="management/login" element={<SuperadminLoginPage />} />
        <Route path="payment" element={<PaymentRouteGate />} />
        <Route path="email-verification" element={<EmailVerificationPage />} />
        <Route path="forgot-password" element={<ForgotPasswordPage />} />
        <Route path="reset-password" element={<ResetPasswordPage />} />
        <Route path="logout" element={<LogoutPage />} />
        <Route path="cookies-policy" element={<CookiesPolicyPage />} />
        <Route path="terms" element={<TermsPage />} />
        <Route path="privacy" element={<PrivacyPolicyPage />} />
        {/* JUB-105: point-of-collection GDPR notice for RSVP/gift consent links */}
        <Route path="data-processing-notice" element={<DataProcessingNoticePage />} />
        {/* JUB-123 / JUB-100: public product guides and FAQ */}
        <Route path="blog" element={<BlogIndexPage />} />
        <Route path="blog/:slug" element={<BlogArticlePage />} />
        <Route path="faq" element={<FaqPage />} />

        {/* Public campaign routes (discounts/vouchers) */}
        <Route path="campaign/:uuid/questionnaire" element={<CampaignQuestionnairePage />} />
        <Route path="campaign/:uuid/claim" element={<CampaignClaimPage />} />

              {/* Developer documentation routes (only when VITE_ENABLE_DEV_DOCS is enabled) */}
              {import.meta.env.VITE_ENABLE_DEV_DOCS === "true" && (
                <>
                  <Route path="dev/rsvp-docs" element={<RSVPDocumentationPage />} />
                  <Route path="dev/gift-contributions-docs" element={<GiftContributionsDocumentationPage />} />
                </>
              )}

        {/* User routes (regular customers - protected) */}
        <Route path="evenimente" element={<AdminDashboard />} />
        <Route path="events" element={<AdminDashboard />} />
        <Route path="editeaza-eveniment/:id" element={<EventManagement />} />
        <Route path="edit-event/:id" element={<EventManagement />} />
        {/* JUB-154: event creation wizard */}
        <Route path="eveniment-nou" element={<OnboardingWizardPage />} />
        <Route path="eveniment-nou/:uuid/:step" element={<OnboardingWizardPage />} />
        <Route path="new-event" element={<OnboardingWizardPage />} />
        <Route path="new-event/:uuid/:step" element={<OnboardingWizardPage />} />
        
        {/* Public event routes */}
        <Route path="events/:slug" element={<EventPublicPage />} />
        <Route path="evenimente/:slug" element={<EventPublicPage />} />
        {/* Direct event slug route (without prefix) - must come after specific routes */}
        <Route path=":slug" element={<EventPublicPage />} />
        <Route path="plati" element={<PaymentsRedirect />} />
        <Route path="payments" element={<PaymentsRedirect />} />
        <Route path="pachete" element={<PachetePage />} />
        <Route path="payment-settings" element={<PachetePage />} />
        <Route path="conturi-conectate" element={<PaymentSettingsRouteGate />} />
        <Route path="connected-accounts" element={<PaymentSettingsRouteGate />} />
        <Route path="setari" element={<AccountSettings />} />
        <Route path="settings" element={<AccountSettings />} />

        {/* Administrator routes (Jubiel employees only - protected, all use /management/* prefix) */}
        <Route path="management/templates" element={<RequireSuperadmin><TemplateManagement /></RequireSuperadmin>} />
        <Route path="management/events" element={<RequireSuperadmin><EventsListing /></RequireSuperadmin>} />
        <Route path="management/users/:userId" element={<RequireSuperadmin><SuperadminUserProfile /></RequireSuperadmin>} />
        <Route path="management/campaigns" element={<RequireSuperadmin><CampaignManagement /></RequireSuperadmin>} />
        <Route path="management/campaigns/:uuid" element={<RequireSuperadmin><CampaignEditor /></RequireSuperadmin>} />
        <Route path="management" element={<RequireSuperadmin><Management /></RequireSuperadmin>} />
        {/* JUB-184: anything deeper that no route above claims */}
        <Route path="*" element={<NotFound />} />
      </Route>


      {/* Root redirect - redirect / to /ro */}
      <Route path="/" element={<LanguageRoute />} />

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default AppRoutes;

